import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { FormControlLabel, InputAdornment, MenuItem, Switch, TextField } from "@mui/material";
import { Edit2, Eye, Plus, Search } from "react-feather";
import { withSuperAdminAuth } from "../../../components/GetAuth";
import PageHeader from "../../../components/PageHeader";
import TablePaginationComponent from "../../../components/TablePagination";
import { ADDON_NAMES, formatGBP2 } from "../../../components/Features";
import { useWebsite } from "../../../../context/WebsiteContext";
import { useSiteBranding } from "../../../../hooks/useSiteBranding";
import { ConfirmDialog, fmtDate, saGet, saPost, StatusChip, TableSkeleton, Toast, useToast } from "../shared";
import styles from "../superadmin.module.css";

const SHORT_ADDON = {
  route_optimization: "Routes", live_tracking: "Tracking", membership: "Membership", referral: "Referral",
  driver_app: "Driver app", customer_app: "Customer app", air_freight: "Air", shipping_delivery: "Shipping",
};

// All companies with billing state; filters live in the URL so links like ?overdue=1 work
const CompanyList = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [confirm, setConfirm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [toast, showMessage, closeToast] = useToast();
  const [search, setSearch] = useState(searchParams.get("search") || "");

  const status = searchParams.get("status") || "all";
  const addon = searchParams.get("addon") || "";
  const overdue = searchParams.get("overdue") === "1";
  const sort = searchParams.get("sort") || "created";
  const page = parseInt(searchParams.get("page") || "0", 10);
  const perPage = parseInt(searchParams.get("per_page") || "25", 10);

  const { websiteData } = useWebsite();
  useSiteBranding("Companies", websiteData);

  const setParam = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value === "" || value === null || value === "all" || value === false) next.delete(key); else next.set(key, value);
    if (key !== "page") next.delete("page");
    setSearchParams(next, { replace: true });
  };

  // Debounced search box
  useEffect(() => {
    const t = setTimeout(() => {
      if ((searchParams.get("search") || "") !== search) setParam("search", search);
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const load = () => {
    setLoading(true);
    saGet("companies", { search: searchParams.get("search") || "", status, addon, overdue: overdue ? 1 : 0, sort, dir: sort === "name" ? "asc" : "desc", page: page + 1, per_page: perPage }).then((response) => {
      setLoading(false);
      if (response.data.status === "SUCCESS") {
        setRows(response.data.data.data);
        setTotal(response.data.data.total);
      }
    }).catch(() => setLoading(false));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(load, [searchParams]);

  const runStatus = () => {
    setBusy(true);
    saPost(`companies/${confirm.id}/status`, { action: confirm.action }).then((response) => {
      setBusy(false);
      setConfirm(null);
      showMessage(response.data.message, response.data.status === "SUCCESS" ? "success" : "error");
      if (response.data.status === "SUCCESS") load();
    }).catch(() => { setBusy(false); setConfirm(null); });
  };

  return (<div className={`${styles.Page}`}>
    <PageHeader title="Companies" trail={[{ label: "Platform", to: "/super-admin/dashboard" }]} />

    <div className={`${styles.Toolbar}`}>
      <TextField
        className={`${styles.Search}`}
        size="small"
        placeholder="Search name, domain, email"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        InputProps={{ startAdornment: (<InputAdornment position="start"><Search size={16} /></InputAdornment>) }}
      />
      <TextField className={`${styles.Filter}`} size="small" select label="Status" value={status} onChange={(e) => setParam("status", e.target.value)}>
        <MenuItem value="all">All statuses</MenuItem>
        <MenuItem value="trial">Trial</MenuItem>
        <MenuItem value="active">Active</MenuItem>
        <MenuItem value="suspended">Suspended</MenuItem>
        <MenuItem value="cancelling">Unsubscribed (ending)</MenuItem>
        <MenuItem value="cancelled">Cancelled</MenuItem>
      </TextField>
      <TextField className={`${styles.Filter}`} size="small" select label="Add-on" value={addon} onChange={(e) => setParam("addon", e.target.value)}>
        <MenuItem value="">Any add-on</MenuItem>
        {Object.keys(ADDON_NAMES).map((key) => <MenuItem key={key} value={key}>{ADDON_NAMES[key]}</MenuItem>)}
      </TextField>
      <TextField className={`${styles.Filter}`} size="small" select label="Sort" value={sort} onChange={(e) => setParam("sort", e.target.value)}>
        <MenuItem value="created">Newest</MenuItem>
        <MenuItem value="name">Name</MenuItem>
        <MenuItem value="next_bill">Next bill</MenuItem>
        <MenuItem value="status">Status</MenuItem>
      </TextField>
      <FormControlLabel control={<Switch checked={overdue} onChange={(e) => setParam("overdue", e.target.checked ? "1" : "")} />} label="Overdue only" />
      <div className={`${styles.ToolbarRight}`}>
        <Link to={"/super-admin/companies/add"} className={`${styles.ActionBU} ${styles.ActionPrimary}`}><Plus />Add company</Link>
      </div>
    </div>

    {loading && <TableSkeleton rows={8} />}

    {!loading && (<div className={`${styles.TableWrap}`}>
      <table className={`${styles.Table}`}>
        <thead>
          <tr>
            <th>Company</th>
            <th>Owner</th>
            <th>Status</th>
            <th>Trial end / Next bill</th>
            <th>Add-ons</th>
            <th className={`${styles.Right}`}>Monthly</th>
            <th className={`${styles.Right}`}>Outstanding</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((c) => (<tr key={c.id}>
            <td>
              <Link to={`/super-admin/companies/${c.id}`} className={`${styles.RowLink}`}>{c.company_name}</Link>
              <span className={`${styles.SubText}`}>{c.domain || "No domain"}</span>
            </td>
            <td>{c.name}<span className={`${styles.SubText}`}>{c.owner_email || c.email}</span></td>
            <td>
              <StatusChip status={c.billing_status} />
              {c.suspends_on && <span className={`${styles.SubText} ${styles.Danger}`}>Suspends {fmtDate(c.suspends_on)}</span>}
              {c.cancels_at && c.billing_status !== "cancelled" && <span className={`${styles.SubText} ${styles.Danger}`}>Cancels {fmtDate(c.cancels_at)}</span>}
            </td>
            <td>{c.billing_status === "cancelled" ? `Ended ${fmtDate(c.cancels_at)}` : c.billing_status === "trial" ? `Trial ends ${fmtDate(c.trial_ends_at)}` : fmtDate(c.next_billing_date)}</td>
            <td>
              <div className={`${styles.AddonChips}`}>
                {c.addons.length === 0 && <span className={`${styles.Muted}`}>Basic only</span>}
                {c.addons.map((a) => <span key={a.key} className={`${styles.AddonChip}`} title={a.name}>{SHORT_ADDON[a.key] || a.name}</span>)}
              </div>
            </td>
            <td className={`${styles.Right}`}>{formatGBP2(c.monthly_estimate)}</td>
            <td className={`${styles.Right} ${c.outstanding > 0 ? styles.Danger : ""}`}>{c.outstanding > 0 ? formatGBP2(c.outstanding) : "—"}</td>
            <td>
              <div className={`${styles.Actions}`}>
                <button type="button" className={`${styles.ActionBU}`} onClick={() => navigate(`/super-admin/companies/${c.id}`)} aria-label={`View ${c.company_name}`}><Eye /></button>
                <button type="button" className={`${styles.ActionBU}`} onClick={() => navigate(`/super-admin/companies/${c.id}/edit`)} aria-label={`Edit ${c.company_name}`}><Edit2 /></button>
                {c.billing_status === "suspended" || c.billing_status === "cancelled"
                  ? <button type="button" className={`${styles.ActionBU}`} onClick={() => setConfirm({ id: c.id, action: "activate", name: c.company_name, cancelled: c.billing_status === "cancelled" })}>{c.billing_status === "cancelled" ? "Resubscribe" : "Activate"}</button>
                  : <button type="button" className={`${styles.ActionBU} ${styles.ActionDanger}`} onClick={() => setConfirm({ id: c.id, action: "suspend", name: c.company_name })}>Suspend</button>}
              </div>
            </td>
          </tr>))}
          {rows.length === 0 && <tr><td colSpan={8} style={{ textAlign: "center", padding: 30 }}>No companies match these filters.</td></tr>}
        </tbody>
      </table>
      {total > 0 && <div className={`${styles.Pagination}`}>
        <table><tbody><tr>
          <TablePaginationComponent count={total} colSpan={8} page={page} rowsPerPage={perPage} handleChangePage={(e, p) => setParam("page", String(p))} handleChangeRowsPerPage={(e) => setParam("per_page", e.target.value)} />
        </tr></tbody></table>
      </div>}
    </div>)}

    <ConfirmDialog
      open={confirm}
      busy={busy}
      title={confirm?.action === "suspend" ? `Suspend ${confirm?.name}?` : confirm?.cancelled ? `Resubscribe ${confirm?.name}?` : `Activate ${confirm?.name}?`}
      message={confirm?.action === "suspend"
        ? "The company, its staff, drivers and customers lose access immediately (web and apps). A manual suspension is only lifted by you, not by a payment."
        : confirm?.cancelled
          ? "The subscription restarts today with no new trial: the company is billed monthly from today and its add-ons start switched off."
          : "Access is restored straight away for the company and its apps."}
      confirmLabel={confirm?.action === "suspend" ? "Suspend" : "Activate"}
      onConfirm={runStatus}
      onClose={() => setConfirm(null)}
    />
    <Toast toast={toast} onClose={closeToast} />
  </div>);
}

export default withSuperAdminAuth(CompanyList);
